import {
  buildBgCatalogIndex,
  firstBgImage,
  lookupBgImage,
  type BgCatalogIndex,
  type CatalogBackgroundLike,
} from "../../libs/bgCatalogIndex";
import {findBgUrl, getThumbnailUrl, isPngUrl} from "../../libs/bg-utils";
import {getGdnBackgroundThumbnailUrl} from "../../libs/gdn-bg";

/** One numbered tile of the background gallery. */
export interface GalleryTile {
  /** The background's identity: what `bgOption` holds (libs/bgNumbering.ts). */
  variation: number;
  /** The tile image (WebP thumbnail). */
  thumbnailUrl: string;
  /** The full-size source, for the download button. */
  imageUrl: string;
}

/**
 * The numbered tiles of a gallery, in display order. The background selector
 * draws them, and the Defaults panel lists them for the visibility setting,
 * so both show the same backgrounds.
 *
 * On the catalog path the tiles follow `displayOrder` and each tile is keyed
 * by its variation. Elsewhere the legacy url array is both the order and the
 * key (index + 1). A background with no image or no thumbnail for the
 * product gets no tile, and of two backgrounds with one source image only the
 * first gets a tile.
 */
export function galleryTiles({
  catalogIndex,
  displayOrder,
  allBgUrls,
  product,
  isAllProducts,
  isGoogleDisplayNetwork,
}: {
  catalogIndex: BgCatalogIndex | null;
  /** Catalog variations in display order, or null off the catalog path. */
  displayOrder: number[] | null;
  /** The legacy url arrays, indexed by variation - 1. */
  allBgUrls: unknown[] | null | undefined;
  product: string;
  isAllProducts: boolean;
  isGoogleDisplayNetwork: boolean;
}): GalleryTile[] {
  const urls = allBgUrls ?? [];
  const variations = catalogIndex && displayOrder ? displayOrder : urls.map((_, i) => i + 1);
  const tiles: GalleryTile[] = [];
  const addedSources = new Set<string>();

  for (const variation of variations) {
    // Full-size source + thumbnail for this variation's tile button. With the
    // catalog index published (#567, B3) both come from the catalog: rewriting
    // `tiles/x.png` into `gdrive_thumbnails/x.webp` stops working once the
    // bytes live under content-hashed keys. ALL-products mode takes the
    // variation's first entry — the same tile the legacy "first PNG of the
    // set" (the `#<hash>` default) resolved to.
    let imageUrl: string | undefined;
    let thumbnailUrl: string | undefined;
    if (catalogIndex) {
      const entry = isAllProducts
        ? firstBgImage(catalogIndex, variation)
        : lookupBgImage(catalogIndex, variation, product);
      imageUrl = entry?.url || undefined;
      thumbnailUrl = entry?.thumbnailUrl;
    } else {
      // For "All Products" mode, use the first URL from each bg set for thumbnails
      const set = urls[variation - 1];
      imageUrl = isAllProducts
        ? Array.isArray(set)
          ? set.find((u: string) => isPngUrl(u))
          : undefined
        : findBgUrl(urls, variation, `/${product}`);
      thumbnailUrl = imageUrl
        ? isGoogleDisplayNetwork
          ? getGdnBackgroundThumbnailUrl(imageUrl)
          : getThumbnailUrl(imageUrl)
        : undefined;
    }

    // Crops can share a thumbnail. Keep their distinct source markers.
    if (!imageUrl || addedSources.has(imageUrl)) continue;
    addedSources.add(imageUrl);
    if (thumbnailUrl) tiles.push({variation, thumbnailUrl, imageUrl});
  }
  return tiles;
}

/**
 * The numbered tiles that the background selector draws on a page: the
 * catalog gallery of the page, or on a saved link the published gallery of
 * the link's product (App's `linkGallery`). App numbers the same tiles for
 * the URL (libs/bgNumbering.ts), so a number is always the place of a tile.
 */
export function pageGalleryTiles({
  catalogIndex,
  displayOrder,
  linkGallery,
  allBgUrls,
  product,
  isAllProducts,
  isGoogleDisplayNetwork,
}: {
  /** The page's published catalog index, or null off the catalog path. */
  catalogIndex: BgCatalogIndex | null;
  displayOrder: number[] | null;
  linkGallery: CatalogBackgroundLike[] | null;
  allBgUrls: unknown[] | null | undefined;
  product: string;
  isAllProducts: boolean;
  isGoogleDisplayNetwork: boolean;
}): GalleryTile[] {
  const linkIndex = linkGallery ? buildBgCatalogIndex(linkGallery, product) : null;
  return galleryTiles({
    catalogIndex: catalogIndex ?? linkIndex,
    displayOrder: displayOrder ?? linkGallery?.map((background) => background.variation) ?? null,
    allBgUrls: linkGallery !== null ? [] : allBgUrls,
    product,
    isAllProducts,
    isGoogleDisplayNetwork,
  });
}
